
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PresupuestoWithId, InvoiceItem, Cliente, Producto, Tax, PaymentMethod } from '@shared/types';
import { useUser } from '@/contexts/UserContext';
import { formatearPorcentajeIrpf } from '@/utils/porcentaje';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { TrashIcon, PlusIcon, SaveIcon, BanIcon, Loader2Icon, EyeIcon, DownloadIcon } from '@/components/Icons';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DocumentPreview } from '@/components/ui/DocumentPreview';
import { generatePDF } from '@/utils/pdfGenerator';

const IVA_EXENTO_NOTICE = "Concepto exento de IVA - Art. 20. Uno. 26º (Ley 37/1992)";

export default function PresupuestoForm() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { fetchWithAuth, userProfile } = useUser();

    const [presupuesto, setPresupuesto] = useState<Partial<PresupuestoWithId>>({});
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [productos, setProductos] = useState<Producto[]>([]);
    const [impuestos, setImpuestos] = useState<Tax[]>([]);
    const [metodosPago, setMetodosPago] = useState<PaymentMethod[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    // Ref para evitar re-disparos del useEffect de IVA exento
    const lastExentoCheck = useRef<boolean | null>(null);

    // Detectar parámetro showPreview en la URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('showPreview') === 'true') {
            setShowPreview(true);
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    const createNewItem = useCallback((taxes: Tax[]): InvoiceItem => ({
        id: crypto.randomUUID(),
        concepto: '',
        cantidad: 1,
        precioUnitario: 0,
        taxId: taxes.find(t => t.isDefault)?.id || taxes[0]?.id || '',
    }), []);

    // Migrar items legacy (precio/iva) al formato correcto (precioUnitario/taxId)
    const migrateItems = useCallback((items: any[], taxes: Tax[]): InvoiceItem[] => {
        return items.map(item => {
            // Si ya tiene precioUnitario y taxId, no migrar
            if (item.precioUnitario !== undefined && item.taxId) return item;

            // Migrar precio → precioUnitario
            const precioUnitario = item.precioUnitario ?? item.precio ?? 0;

            // Migrar iva (rate numérico) → taxId (referencia)
            let taxId = item.taxId || '';
            if (!taxId && item.iva !== undefined) {
                const matchingTax = taxes.find(t => Math.abs(t.rate - item.iva) < 0.001);
                taxId = matchingTax?.id || taxes.find(t => t.isDefault)?.id || taxes[0]?.id || '';
            }

            return { ...item, precioUnitario, taxId };
        });
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const fetches: Promise<Response>[] = [
                    fetchWithAuth('/api/clientes'),
                    fetchWithAuth('/api/productos'),
                    fetchWithAuth('/api/impuestos'),
                    fetchWithAuth('/api/metodos-pago'),
                ];
                // Solo pedir next-number al crear (B8)
                if (!id) {
                    fetches.push(fetchWithAuth('/api/presupuestos/next-number'));
                }

                const responses = await Promise.all(fetches);
                const [clientesData, productosData, impuestosData, metodosData] = await Promise.all(
                    responses.slice(0, 4).map(r => r.json())
                );

                setClientes(clientesData);
                setProductos(productosData);
                setImpuestos(impuestosData);
                setMetodosPago(metodosData);

                if (id) {
                    const presupuestoRes = await fetchWithAuth(`/api/presupuestos/${id}`);
                    if (!presupuestoRes.ok) throw new Error('Presupuesto no encontrado');
                    const presupuestoData = await presupuestoRes.json();
                    // Migrar items legacy si es necesario
                    if (presupuestoData.items) {
                        presupuestoData.items = migrateItems(presupuestoData.items, impuestosData);
                    }
                    setPresupuesto(presupuestoData);
                } else {
                    const { nextNumber } = await responses[4].json();
                    setPresupuesto({
                        numero: nextNumber,
                        clienteId: '',
                        fecha: new Date().toISOString().slice(0, 10),
                        vencimiento: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                        items: [createNewItem(impuestosData)],
                        estado: 'Pendiente',
                        notas: '',
                        aplicarIrpf: false,
                        total: 0,
                    });
                }
                setHasChanges(false);
            } catch (error) {
                toast.error('Error al cargar datos', { description: error instanceof Error ? error.message : 'Error desconocido' });
                navigate('/presupuestos');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [id, navigate, fetchWithAuth, createNewItem, migrateItems]);

    const handleFieldChange = useCallback((field: keyof PresupuestoWithId, value: any) => {
        setPresupuesto(prev => ({...prev, [field]: value}));
        if (!hasChanges) setHasChanges(true);
    }, [hasChanges]);

    const handleItemChange = (itemId: string, updates: Partial<InvoiceItem>) => {
        const newItems = presupuesto.items?.map(item =>
            item.id === itemId ? { ...item, ...updates } : item
        );
        handleFieldChange('items', newItems);
    };

    const addNewItem = () => handleFieldChange('items', [...(presupuesto.items || []), createNewItem(impuestos)]);
    const removeItem = (itemId: string) => handleFieldChange('items', presupuesto.items?.filter(item => item.id !== itemId));

    const [savingProductIds, setSavingProductIds] = useState<Set<string>>(new Set());

    const productOptions = useMemo(() => productos.map(p => ({ label: p.descripcion || p.nombre, value: p.descripcion || p.nombre })), [productos]);

    // Comprobar si un concepto corresponde a un producto existente
    const isExistingProduct = useCallback((concepto: string) => {
        if (!concepto) return true; // concepto vacío no se muestra como "guardar"
        return productos.some(p =>
            (p.descripcion || p.nombre).toLowerCase() === concepto.toLowerCase()
        );
    }, [productos]);

    // Guardar un ítem como producto nuevo en la base de datos
    const handleSaveAsProduct = async (item: InvoiceItem) => {
        if (!item.concepto) return;

        setSavingProductIds(prev => new Set(prev).add(item.id));
        try {
            const res = await fetchWithAuth('/api/productos', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombre: item.concepto,
                    descripcion: item.concepto,
                    precio: item.precioUnitario,
                    precioUnitario: item.precioUnitario,
                    taxId: item.taxId,
                }),
            });
            if (!res.ok) throw new Error('No se pudo guardar el producto');

            const newProduct = await res.json();
            setProductos(prev => [...prev, newProduct]);
            toast.success('Producto guardado', { description: `"${item.concepto}" añadido a tu catálogo.` });
        } catch (error) {
            toast.error('Error al guardar producto', { description: error instanceof Error ? error.message : 'Error desconocido' });
        } finally {
            setSavingProductIds(prev => {
                const next = new Set(prev);
                next.delete(item.id);
                return next;
            });
        }
    };

    const impuestosMap = useMemo(() => new Map(impuestos.map(t => [t.id, t.rate])), [impuestos]);

    const { subtotal, iva, irpf, total } = useMemo(() => {
        const sub = presupuesto.items?.reduce((acc, item) => acc + item.cantidad * item.precioUnitario, 0) ?? 0;
        const ivaAmount = presupuesto.items?.reduce((acc, item) => {
            const rate = impuestosMap.get(item.taxId) ?? 0;
            return acc + item.cantidad * item.precioUnitario * rate;
        }, 0) ?? 0;
        // Tasa del perfil del emisor (0 si no está definida), igual que el servidor
        const irpfRate = userProfile?.irpfRate ?? 0;
        const irpfAmount = presupuesto.aplicarIrpf ? sub * irpfRate : 0;
        return { subtotal: sub, iva: ivaAmount, irpf: irpfAmount, total: sub + ivaAmount - irpfAmount };
    }, [presupuesto.items, presupuesto.aplicarIrpf, userProfile?.irpfRate, impuestosMap]);

    // Detectar IVA Exento y añadir aviso automáticamente (B10: solo reaccionar a cambios de items)
    useEffect(() => {
        if (!presupuesto.items) return;

        const hasExentoTax = presupuesto.items.some(item => {
            const rate = impuestosMap.get(item.taxId) ?? -1;
            return rate === 0;
        });

        // Solo actuar si el estado cambió respecto al anterior
        if (hasExentoTax === lastExentoCheck.current) return;
        lastExentoCheck.current = hasExentoTax;

        const currentNotas = presupuesto.notas || '';
        const hasExentoNotice = currentNotas.includes(IVA_EXENTO_NOTICE);

        if (hasExentoTax && !hasExentoNotice) {
            const newNotas = currentNotas
                ? `${currentNotas}\n\n${IVA_EXENTO_NOTICE}`
                : IVA_EXENTO_NOTICE;
            handleFieldChange('notas', newNotas);
        }
    }, [presupuesto.items, impuestosMap]); // NO depende de presupuesto.notas

    const handleSave = async () => {
        if (!presupuesto.clienteId) {
            toast.warning('Validación fallida', { description: 'Debes seleccionar un cliente.' });
            return;
        }

        setIsSaving(true);
        try {
            const dataToSend = { ...presupuesto, total };
            const response = await fetchWithAuth(id ? `/api/presupuestos/${id}` : '/api/presupuestos', {
                method: id ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al guardar');
            }
            toast.success(`Presupuesto ${id ? 'actualizado' : 'creado'} con éxito`);
            setHasChanges(false);
            navigate('/presupuestos');
        } catch (error) {
            toast.error('Error al guardar', { description: error instanceof Error ? error.message : 'Ocurrió un error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => hasChanges ? setShowUnsavedDialog(true) : navigate('/presupuestos');

    if (isLoading) return <div className="flex justify-center items-center h-64"><Loader2Icon className="animate-spin h-8 w-8 text-primary" /></div>;

    const selectedCliente = clientes.find(c => c.id === presupuesto.clienteId);
    const selectedPaymentMethod = metodosPago.find(m => m.id === presupuesto.paymentMethodId);

    return (
    <>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold tracking-tighter">{id ? `Editar Presupuesto #${presupuesto.numero}` : 'Nuevo Presupuesto'}</h1>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCancel} disabled={isSaving}><BanIcon className="mr-2" />Cancelar</Button>
                    <Button variant="outline" onClick={() => setShowPreview(true)} disabled={!presupuesto.clienteId}><EyeIcon className="mr-2" />Vista Previa</Button>
                    <Button onClick={handleSave} disabled={isSaving || !hasChanges}><SaveIcon className="mr-2" />{isSaving ? 'Guardando...' : 'Guardar'}</Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader><CardTitle>Información General</CardTitle></CardHeader>
                        <CardContent className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label>Nº Presupuesto</Label>
                                <Input value={presupuesto.numero ?? ''} onChange={e => handleFieldChange('numero', e.target.value)} placeholder="Ej: 2026-0001" />
                            </div>
                            <div className="space-y-2">
                                <Label>Cliente</Label>
                                <Select value={presupuesto.clienteId ?? ''} onValueChange={(val) => handleFieldChange('clienteId', val)}>
                                    <SelectTrigger><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                                    <SelectContent>{clientes.map(c => <SelectItem key={c.id} value={c.id!}>{c.nombre}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                             <div className="space-y-2">
                                <Label>Estado</Label>
                                <Select value={presupuesto.estado ?? ''} onValueChange={(val) => handleFieldChange('estado', val)}>
                                    <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Pendiente">Pendiente</SelectItem>
                                        <SelectItem value="Aceptado">Aceptado</SelectItem>
                                        <SelectItem value="Rechazado">Rechazado</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Fecha</Label>
                                <Input type="date" value={presupuesto.fecha ?? ''} onChange={e => handleFieldChange('fecha', e.target.value)} />
                            </div>
                             <div className="space-y-2">
                                <Label>Vencimiento</Label>
                                <Input type="date" value={presupuesto.vencimiento ?? ''} onChange={e => handleFieldChange('vencimiento', e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Método de Pago</Label>
                                <Select value={presupuesto.paymentMethodId ?? ''} onValueChange={(val) => handleFieldChange('paymentMethodId', val)}>
                                    <SelectTrigger><SelectValue placeholder="Seleccionar método" /></SelectTrigger>
                                    <SelectContent>
                                        {metodosPago.map(m => <SelectItem key={m.id} value={m.id!}>{m.nombre}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader><CardTitle>Conceptos</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            {presupuesto.items?.map((item) => (
                                <div key={item.id} className="p-4 bg-muted/30 rounded-lg border grid md:grid-cols-12 gap-4 relative">
                                    <div className="md:col-span-4 space-y-2">
                                        <Label>Concepto</Label>
                                        <Combobox options={productOptions} value={item.concepto} placeholder="Buscar o escribir concepto..." onChange={val => {
                                            const product = productos.find(p => (p.descripcion || p.nombre) === val);
                                            const updates: Partial<InvoiceItem> = { concepto: val };
                                            if (product) {
                                                updates.precioUnitario = product.precioUnitario ?? product.precio;
                                                if (product.taxId) updates.taxId = product.taxId;
                                            }
                                            handleItemChange(item.id, updates);
                                        }} />
                                        {!isExistingProduct(item.concepto) && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-xs h-6 px-2 text-muted-foreground hover:text-primary"
                                                disabled={savingProductIds.has(item.id)}
                                                onClick={() => handleSaveAsProduct(item)}
                                            >
                                                {savingProductIds.has(item.id)
                                                    ? <><Loader2Icon className="h-3 w-3 mr-1 animate-spin" />Guardando...</>
                                                    : <><SaveIcon className="h-3 w-3 mr-1" />Guardar como producto</>
                                                }
                                            </Button>
                                        )}
                                    </div>
                                    <div className="md:col-span-2 space-y-2">
                                        <Label>Cantidad</Label>
                                        <Input type="number" value={item.cantidad} onChange={e => handleItemChange(item.id, { cantidad: parseFloat(e.target.value) || 0 })} />
                                    </div>
                                    <div className="md:col-span-2 space-y-2">
                                        <Label>Precio</Label>
                                        <Input type="number" value={item.precioUnitario} onChange={e => handleItemChange(item.id, { precioUnitario: parseFloat(e.target.value) || 0 })} />
                                    </div>
                                    <div className="md:col-span-3 space-y-2">
                                        <Label>Impuesto</Label>
                                        <Select value={item.taxId} onValueChange={val => handleItemChange(item.id, { taxId: val })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {impuestos.map(t => (
                                                    <SelectItem key={t.id} value={t.id}>{t.nombre} ({(t.rate * 100).toFixed(0)}%)</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="md:col-span-1 flex items-end">
                                        <Button variant="ghost" size="icon" onClick={() => removeItem(item.id)}><TrashIcon className="text-destructive" /></Button>
                                    </div>
                                </div>
                            ))}
                             <Button onClick={addNewItem} variant="outline"><PlusIcon className="mr-2"/>Añadir línea</Button>
                        </CardContent>
                    </Card>
                </div>

                 <div className="lg:sticky lg:top-24 space-y-6">
                    <Card>
                        <CardHeader><CardTitle>Resumen</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{subtotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span>{iva.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>
                            {presupuesto.aplicarIrpf && <div className="flex justify-between"><span className="text-muted-foreground">IRPF ({formatearPorcentajeIrpf(userProfile?.irpfRate)}%)</span><span className="text-destructive">-{irpf.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>}
                            <div className="text-xl font-bold flex justify-between pt-2 border-t"><span>Total</span><span>{total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>
                            <div className="flex items-center space-x-2 pt-4">
                                <Checkbox id="irpf" checked={presupuesto.aplicarIrpf} onCheckedChange={(checked) => handleFieldChange('aplicarIrpf', !!checked)} />
                                <Label htmlFor="irpf">Aplicar retención IRPF ({formatearPorcentajeIrpf(userProfile?.irpfRate)}%)</Label>
                            </div>
                        </CardContent>
                    </Card>
                     <Card>
                        <CardHeader><CardTitle>Notas</CardTitle></CardHeader>
                        <CardContent>
                            <Textarea value={presupuesto.notas ?? ''} onChange={e => handleFieldChange('notas', e.target.value)} placeholder="Notas para el cliente..." />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
        <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
            <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Cambios sin guardar</AlertDialogTitle><AlertDialogDescription>Tienes cambios sin guardar. ¿Seguro que quieres salir?</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>No, quedarme</AlertDialogCancel>
                    <AlertDialogAction onClick={() => navigate('/presupuestos')}>Sí, descartar cambios</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>

        {/* Diálogo de Vista Previa */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex justify-between items-center">
                        <DialogTitle>Vista Previa - Presupuesto {presupuesto.numero}</DialogTitle>
                        <Button variant="outline" size="sm" onClick={() => generatePDF('document-preview-pdf', `presupuesto-${presupuesto.numero}.pdf`)}>
                            <DownloadIcon className="mr-2 h-4 w-4" />Descargar PDF
                        </Button>
                    </div>
                    <DialogDescription className="sr-only">
                        Vista previa del documento de presupuesto antes de imprimir o descargar en PDF
                    </DialogDescription>
                </DialogHeader>
                {selectedCliente && userProfile && presupuesto.items && (
                    <DocumentPreview
                        document={{
                            ...presupuesto,
                            tipo: 'PRESUPUESTO',
                            items: presupuesto.items || []
                        } as any}
                        client={selectedCliente}
                        freelance={userProfile}
                        taxes={impuestos}
                        paymentMethod={selectedPaymentMethod}
                    />
                )}
                <div className="flex justify-center pt-4 border-t">
                    <Button variant="outline" onClick={() => generatePDF('document-preview-pdf', `presupuesto-${presupuesto.numero}.pdf`)}>
                        <DownloadIcon className="mr-2 h-4 w-4" />Descargar PDF
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    </>
    );
}
